import { createEffect, createSignal, onMount } from 'solid-js'
import { toast } from 'common'
import { visitorCheckin as api, focus as focusApi, OrgYearInfo } from 'st-app/api'
import Button from '../../st-button/st-button'
import STInput from '../../st-input/st-input'
import Flexbox from '../../st-flexbox/st-flexbox'
import OrgSearchInput from './org-search-input'
import { requireVisitorCheckinContext } from '../VisitorCheckinContext'

type StationRegistrationStepProps = {
    onRegistered: () => void;
}

export function StationRegistrationStep(props: StationRegistrationStepProps) {
    const checkin = requireVisitorCheckinContext();

    const [stationName, setStationName] = createSignal('');

    createEffect(() => {
        const name = checkin.machineInfo()?.machineName;
        if (name && !stationName()) setStationName(name);
    });
    const [organizationGU, setOrganizationGU] = createSignal('');
    const [organizationName, setOrganizationName] = createSignal('');
    const [isSubmitting, setIsSubmitting] = createSignal(false);

    onMount(async () => {
        try {
            const focusInfo = await focusApi.GetFocusInfo();
            const focusOrg = focusInfo.orgYears?.find((oy: OrgYearInfo) => oy.orgYearGU === focusInfo.focusOrgYearGU);
            if (focusOrg) {
                setOrganizationGU(focusOrg.organizationGU ?? '');
                setOrganizationName(focusOrg.organizationName ?? '');
            }
        } catch {
            // Focus info unavailable — leave org blank for manual selection
        }
    });

    const submit = async () => {
        if (!stationName()) {
            toast.notify('error', 'Station name is required.');
            return;
        }
        if (!organizationGU()) {
            toast.notify('error', 'Organization is required.');
            return;
        }

        const macAddress = checkin.machineInfo()?.macAddress;
        if (!macAddress) {
            toast.notify('error', 'Machine information is unavailable. Please ensure the Visitor Management Bridge is running.');
            return;
        }

        setIsSubmitting(true);
        try {
            const vmStationGU = await api.RegisterStation(macAddress, stationName(), organizationGU());
            checkin.setVmStationGU(vmStationGU);
            props.onRegistered();
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while registering this station. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Flexbox dir="vertical" gap="12px" style="padding: 16px;">
            <p>This device has not been registered as a visitor management station. Please provide a name for this station and select the organization it belongs to.</p>

            <Flexbox dir="vertical" gap="4px">
                <label>Station Name</label>
                <STInput value={[stationName, setStationName]} />
            </Flexbox>

            <Flexbox dir="vertical" gap="4px">
                <label>Organization</label>
                <OrgSearchInput value={[organizationGU, setOrganizationGU]} displayName={[organizationName, setOrganizationName]} />
            </Flexbox>

            <Button type="success" icon="fas fa-check" onClick={submit} loading={isSubmitting()}>
                Register Station
            </Button>
        </Flexbox>
    );
}
